import { groupBy, mapValues, sum, uniq, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { distanceEquals } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import {
  getAggregatedTrussStatisticsByBundleGuid,
  getBundlesByProductionGroupGuid,
  getStatusesByBundleGuid,
} from './bundles';
import { getProductionComponentInstancesByProductionGroupGuid } from './getProductionComponentInstancesByProductionGroupGuid';
import { getProjectLayoutModels } from './getProjectLayoutModels';

export const getStatusesByProductionGroupGuid = createSelector(
  (state: RootState) => state.productionGroups,
  getBundlesByProductionGroupGuid,
  getStatusesByBundleGuid,

  (productionGroups, bundlesByProductionGroupGuid, statusesByBundleGuid) =>
    mapValues(productionGroups, (productionGroup) =>
      uniq(bundlesByProductionGroupGuid[productionGroup.guid]?.flatMap((bundle) => statusesByBundleGuid[bundle.guid])),
    ),
);

export const getProductionGroupHasUnbundledOrUnBatchedComponents = createSelector(
  getProductionComponentInstancesByProductionGroupGuid,

  (productionComponentInstancesByProductionGroupGuid): { [productionGroupGuid: string]: boolean } =>
    mapValues(productionComponentInstancesByProductionGroupGuid, (productionComponentInstancesForProductionGroup) =>
      productionComponentInstancesForProductionGroup.some(
        (instance) => instance.bundleGuid === null || instance.batchGuid === null,
      ),
    ),
);

export const getProductionGroupsByProjectGuid = createSelector(
  getProjectLayoutModels,
  (state: RootState) => state.productionGroups,

  (projectLayoutModels, productionGroups) => {
    const projectGuids = projectLayoutModels.map((model) => model.guid);
    const productionGroupsGroupedByProjectGuid = groupBy(
      Object.values(productionGroups),
      (productionGroup) => productionGroup.projectGuid,
    );
    return Object.fromEntries(
      uniq(projectGuids).map((projectGuid) => [projectGuid, productionGroupsGroupedByProjectGuid[projectGuid] ?? []]),
    );
  },
);

export const getAggregatedTrussStatisticsByProductionGroupGuid = createSelector(
  (state: RootState) => state.productionGroups,
  getBundlesByProductionGroupGuid,
  getAggregatedTrussStatisticsByBundleGuid,

  (
    productionGroups,
    bundlesByProductionGroupGuid,
    aggregatedTrussStatisticsByBundleGuid,
  ): { [scheduleGroupGuid: string]: AggregatedTrussStatistics } =>
    mapValues(productionGroups, (productionGroup) => {
      // Do not show values unless all are loaded - we do not want to provide misleading information
      const allTrussesForProductionGroupAreLoaded = bundlesByProductionGroupGuid[productionGroup.guid].every(
        (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses.length > 0,
      );
      if (!allTrussesForProductionGroupAreLoaded)
        return {
          boardFeet: null,
          linearFeet: null,
          trussCount: sum(
            bundlesByProductionGroupGuid[productionGroup.guid]?.flatMap(
              (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
            ),
          ),
          thicknesses: [],
          widths: [],
          heights: [],
        };

      return {
        boardFeet: sum(
          bundlesByProductionGroupGuid[productionGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].boardFeet ?? 0,
          ),
        ),
        linearFeet: sum(
          bundlesByProductionGroupGuid[productionGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].linearFeet ?? 0,
          ),
        ),
        trussCount: sum(
          bundlesByProductionGroupGuid[productionGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
          ),
        ),
        heights: uniqWith(
          bundlesByProductionGroupGuid[productionGroup.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].heights)
            .filter(notNullish),
          distanceEquals,
        ),
        widths: uniqWith(
          bundlesByProductionGroupGuid[productionGroup.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].widths)
            .filter(notNullish),
          distanceEquals,
        ),
        thicknesses: uniqWith(
          bundlesByProductionGroupGuid[productionGroup.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses)
            .filter(notNullish),
          distanceEquals,
        ),
      };
    }),
);
